import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import BoardOverlay from "./BoardOverlay";
import {
    BoardLink,
    LINK_COLORS,
    LinkWeight,
    MAX_LINK_LABEL_LENGTH,
} from "../../domain/types";

type Props = {
    link: BoardLink;
    /** Label limit from the server when available; falls back to the domain limit. */
    maxLabelLength?: number;
    onChangeLabel: (label: string) => void;
    onChangeColor: (color: string) => void;
    onChangeWeight: (weight: LinkWeight) => void;
    onRemove: () => void;
    onClose: () => void;
    portalContainer?: HTMLElement | null;
};

const WEIGHTS: LinkWeight[] = ["low", "medium", "big"];

/** Editor for a link between two figures: label, color and line weight.
 *  Shared by the therapist and patient boards. */
export default function LinkEditorModal({
    link,
    maxLabelLength,
    onChangeLabel,
    onChangeColor,
    onChangeWeight,
    onRemove,
    onClose,
    portalContainer,
}: Props) {
    const { t } = useTranslation();
    return (
        <BoardOverlay container={portalContainer}>
            <div
                className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                onClick={onClose}
            >
                <div
                    className="w-full max-w-sm rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-3 flex items-center justify-between">
                        <h2 className="text-base font-semibold">
                            {t("constellation.link.editTitle")}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={t("common.close", "Close")}
                            className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <input
                        autoFocus
                        type="text"
                        value={link.label}
                        maxLength={maxLabelLength ?? MAX_LINK_LABEL_LENGTH}
                        onChange={(e) => onChangeLabel(e.target.value)}
                        placeholder={t("constellation.link.placeholder")}
                        aria-label={t("constellation.link.editTitle")}
                        className="w-full rounded-md border px-2 py-1 text-sm"
                    />
                    <div className="mt-3 flex items-center gap-2">
                        {LINK_COLORS.map((c) => (
                            <button
                                key={c}
                                type="button"
                                aria-label={c}
                                aria-pressed={link.color === c}
                                onClick={() => onChangeColor(c)}
                                style={{ background: c }}
                                className={`h-7 w-7 rounded-full border-2 ${
                                    link.color === c ? "border-gray-800" : "border-white"
                                }`}
                            />
                        ))}
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-1">
                        {WEIGHTS.map((w) => (
                            <button
                                key={w}
                                type="button"
                                onClick={() => onChangeWeight(w)}
                                aria-pressed={link.weight === w}
                                className={`min-h-[36px] rounded-md border px-2 text-sm ${
                                    link.weight === w
                                        ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                        : "border-gray-200 hover:border-brand-200"
                                }`}
                            >
                                {t(`constellation.link.weight.${w}`)}
                            </button>
                        ))}
                    </div>
                    <div className="mt-4 flex justify-between">
                        <button
                            type="button"
                            onClick={onRemove}
                            className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                        >
                            {t("constellation.list.remove", "Delete")}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
                        >
                            {t("common.done", "Done")}
                        </button>
                    </div>
                </div>
            </div>
        </BoardOverlay>
    );
}
